import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getInitials, timeAgo, formatHandicap } from '../utils/format';
import Icon from './Icon';
import './PostCard.css';

function PostCard({ post, currentUserId, onDelete, toast }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [liked, setLiked] = useState(post.user_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [commentsCount, setCommentsCount] = useState(post.comments_count);

  const handleLike = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount(c => newLiked ? c + 1 : c - 1);
    try { await api.toggleLike(post.id); } catch { setLiked(!newLiked); setLikesCount(c => newLiked ? c - 1 : c + 1); toast.error('Failed to update like'); }
  };

  const handleShowComments = async () => {
    if (!showComments && comments.length === 0) {
      setLoadingComments(true);
      try { const data = await api.getComments(post.id); setComments(data); } catch { toast.error('Failed to load comments'); }
      setLoadingComments(false);
    }
    setShowComments(s => !s);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const comment = await api.addComment(post.id, commentText);
      setComments(c => [...c, comment]);
      setCommentsCount(n => n + 1);
      setCommentText('');
    } catch { toast.error('Failed to post comment'); }
    finally { setSubmittingComment(false); }
  };

  const postTypeLabel = {
    round_log: 'Round',
    score: 'Round',
    photo: 'Photo',
    text: null,
  };

  const isRound = post.post_type === 'round_log' || post.post_type === 'score';

  return (
    <div className="feed-card card">
      {/* Header */}
      <div className="feed-card-header">
        <Link to={`/golfer/${post.author_id}`} className="feed-author">
          <div className="avatar avatar-sm">
            {post.author_photo ? <img src={post.author_photo} alt={post.author_name} /> : getInitials(post.author_name)}
          </div>
          <div>
            <p className="feed-author-name">{post.author_name}</p>
            <p className="feed-author-meta">
              {post.author_handicap != null && <span>Hdcp {formatHandicap(post.author_handicap)} · </span>}
              {timeAgo(post.created_at)}
            </p>
          </div>
        </Link>
        {postTypeLabel[post.post_type] && (
          <span className="feed-type-badge">{postTypeLabel[post.post_type]}</span>
        )}
        {post.author_id === currentUserId && onDelete && (
          <button className="feed-delete-btn" onClick={() => onDelete(post.id)} title="Delete post" aria-label="Delete post"><Icon name="x" size={18} /></button>
        )}
      </div>

      {/* Round log stats */}
      {isRound && post.course_name && (
        <div className="feed-round-stats">
          <div className="feed-round-course"><Icon name="flag" size={15} /> {post.course_name}</div>
          {(post.course_city || post.course_state) && (
            <div className="feed-round-course-loc">{[post.course_city, post.course_state].filter(Boolean).join(', ')}</div>
          )}
          <div className="feed-round-numbers">
            {post.score && (
              <div className="feed-stat">
                <span className="feed-stat-value">{post.score}</span>
                <span className="feed-stat-label">Score</span>
              </div>
            )}
            {post.score_differential != null && (
              <div className="feed-stat">
                <span className="feed-stat-value">
                  {formatHandicap(post.score_differential)}
                </span>
                <span className="feed-stat-label">Differential</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo */}
      {post.photo_url && (
        <div className="feed-photo">
          <img src={post.photo_url} alt="Post" />
        </div>
      )}

      {/* Content */}
      {post.content && <p className="feed-content">{post.content}</p>}

      {/* Actions */}
      <div className="feed-actions">
        <button className={`feed-action-btn ${liked ? 'liked' : ''}`} onClick={handleLike}>
          <Icon name="heart" size={17} filled={liked} />
          <span>{likesCount > 0 ? likesCount : ''} Kudos</span>
        </button>
        <button className="feed-action-btn" onClick={handleShowComments}>
          <Icon name="chat" size={17} />
          <span>{commentsCount > 0 ? commentsCount : ''} Comment{commentsCount !== 1 ? 's' : ''}</span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="feed-comments">
          {loadingComments ? (
            <div className="loading-container" style={{ padding: 16 }}><div className="spinner"></div></div>
          ) : (
            <>
              {comments.map(c => (
                <div key={c.id} className="feed-comment">
                  <div className="avatar avatar-sm" style={{ flexShrink: 0 }}>
                    {c.author_photo ? <img src={c.author_photo} alt={c.author_name} /> : getInitials(c.author_name)}
                  </div>
                  <div className="feed-comment-body">
                    <span className="feed-comment-name">{c.author_name}</span>
                    <span className="feed-comment-text">{c.content}</span>
                    <span className="feed-comment-time">{timeAgo(c.created_at)}</span>
                  </div>
                </div>
              ))}
              <form className="feed-comment-form" onSubmit={handleAddComment}>
                <input
                  className="feed-comment-input"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={!commentText.trim() || submittingComment}>
                  {submittingComment ? '…' : 'Post'}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PostCard;
